import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Auth } from "aws-amplify";
import ModalBox from "components/Modal";
import type { TSettings } from "components/Settings/types";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { resolveCountryLanguage } from "helpers/countryLanguage";
import useUserRole from "hooks/useUserRole";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setLocalSettings, updateCrealoAdminRole, updateMe } from "store/settingsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";

import { useAuth } from "../../../contexts/auth/AuthContext";
import ChipFilter from "../ChipFilter";
import type { FilterOption } from "../FiltersAutocomplete";
import SettingButtons from "../SettingButtons";
import StyledButton from "../StyledButton";
import StyledTextField from "../StyledTextField/StyledTextField";
import SetupMFAModal from "./SetupMFAModal";

const rolesPreviewOptions: FilterOption[] = [
    { id: ROLES.OWNER, name: "Administrateur" },
    { id: ROLES.RIGHTS_MANAGER, name: "Éditeur" },
    {
        id: ROLES.ACCOUNTANT,
        name: "Comptable",
    },
    {
        id: ROLES.VIEW_ONLY,
        name: "Lecture seule",
    },
    {
        id: ROLES.AGREEMENT_ADMIN,
        name: "Administrateur de contrat",
    },
    {
        id: ROLES.AGREEMENT_MANAGER,
        name: "Gestionnaire de contrats",
    },
    {
        id: ROLES.AGREEMENT_CONTRIBUTOR,
        name: "Approbateur de contrats",
    },
];

type TProps = {
    state: TSettings["accountSettings"];
    setState: React.Dispatch<React.SetStateAction<TSettings>>;
};

const AccountSettings = ({ state, setState }: TProps) => {
    const { t, i18n } = useTranslation();
    const { user } = useAuthenticator((context) => [context.user]);
    const dispatch = useDispatch<AppDispatch>();
    const settings = useSelector((state: RootState) => state.settings);
    const { userData } = useAppSelector((state) => state.settings);
    const [openTextField, setOpenTextField] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [showMFA, setShowMFA] = useState(false);
    const [mfaQRCode, setMFAQRCode] = useState("");
    const [mfaVerificationCode, setMFAVerificationCode] = useState("");
    const [jobTitle, setJobTitle] = useState("");

    const defaultRolePreview = rolesPreviewOptions.find((role) => role.id === userData?.role) as FilterOption;
    const [rolePreview, setRolePreview] = useState<FilterOption>(defaultRolePreview);

    const { isAdminCrealo: isCrealoAdmin } = useAuth();
    const isDirty = jobTitle !== userData?.jobTitle;

    const handleChooseRolePreview = (role: FilterOption) => {
        setRolePreview(role);
        dispatch(
            updateCrealoAdminRole({
                userId: userData?.id as number,
                role: role.id as ROLES,
            }),
        );
    };

    const companyCountryCode = useAppSelector((state) => state.settings.companySettings?.countryCode);

    const handleChangeButton = (name: string, value: string) => {
        setState((prev) => ({
            ...prev,
            accountSettings: {
                ...state,
                [name]: value,
            },
        }));
        if (name == "language") {
            i18n.changeLanguage(resolveCountryLanguage(companyCountryCode, value));
            dispatch(setLocalSettings({ ...settings, accountSettings: { language: value } }));
        }
    };

    const handleChangeJobTitle = () => {
        if (!isDirty) return;
        dispatch(updateMe({ jobTitle }));
    };

    useEffect(() => {
        if (!userData) return;
        setJobTitle(userData?.jobTitle ?? "");
    }, [userData]);

    const handleActivateMFA = async () => {
        if (!showMFA) {
            /**
             * Show modal
             * Request qr code to scan
             */
            setShowMFA(true);

            const secretCode = await Auth.setupTOTP(user);
            const str =
                "otpauth://totp/Crealo:" +
                user.attributes?.["email"] +
                "?secret=" +
                secretCode +
                "&issuer=" +
                encodeURIComponent("https://manage.crealo.app/") +
                "&image=" +
                encodeURIComponent(
                    "https://assets-global.website-files.com/5f96c1e75e94f64c995cffee/611fa59c348a796a2bdc1ff4_iconWebflowIpho.png",
                );
            setMFAQRCode(str);
        } else {
            if (!mfaVerificationCode) return;
            /**
             * Second call, modal is shown
             * code might be written and must be verified
             */
            await Auth.verifyTotpToken(user, mfaVerificationCode);

            await Auth.setPreferredMFA(user, "TOTP");

            /**
             * Show success toast
             */
            setShowMFA(false);
        }
    };

    return (
        <div className="account-list">
            <ModalBox mainTitle={t("Settings.AccountMFA")} open={showMFA} onClose={() => setShowMFA(false)}>
                <SetupMFAModal
                    mfaQRCode={mfaQRCode}
                    mfaVerificationCode={mfaVerificationCode}
                    onMFAVerificationCode={setMFAVerificationCode}
                    handleSubmit={handleActivateMFA}
                />
            </ModalBox>
            <h1 className="account-list-title">{t("Settings.Account")}</h1>
            <SettingButtons
                name="language"
                title={t("Settings.AccountLanguage")}
                description={t("Settings.ChooseLanguage")}
                setSetting={handleChangeButton}
                value={state.language}
                options={["fr", "en"]}
            />

            {/**
             * MFA activation settings
             */}
            <div className="setting-buttons">
                <div className="setting-left">
                    <div className="setting-title">{t("Settings.AccountMFA")}</div>
                    <div className="setting-description">{t("Settings.AccountMFAStatus")}</div>
                </div>
                <div className="setting-right">
                    <StyledButton
                        buttonType="primary"
                        disabled={(user as unknown as any).preferredMFA === "SOFTWARE_TOKEN_MFA"}
                        onClick={handleActivateMFA}
                    >
                        {(user as unknown as any).preferredMFA === "NOMFA"
                            ? t("Settings.Activate")
                            : t("Settings.Active")}
                    </StyledButton>
                </div>
            </div>
            {openTextField && (
                <StyledTextField value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} />
            )}

            {/**
             * Agreements signatory publisher job title
             */}
            <div className="setting-text-input">
                <div className="setting-title">{t("Settings.SignatoryTitle")}</div>
                <StyledTextField
                    size="small"
                    style={{ width: "60%", marginTop: "0.2rem" }}
                    value={jobTitle ?? ""}
                    onChange={(e) => setJobTitle(e.target.value)}
                    onBlur={handleChangeJobTitle}
                />
                <div className="setting-description" style={{ color: "#909090", marginTop: "0.4rem" }}>
                    {t("Settings.SignatoryTitleDesc")}
                </div>
            </div>

            {/**
             * Crealo Admin only
             */}
            {isCrealoAdmin && (
                <div style={{ marginTop: "2rem" }}>
                    <div className="setting-left">
                        <div className="setting-title" style={{ color: "var(--brand)" }}>
                            Rôle preview
                        </div>
                        <div
                            className="setting-description"
                            style={{ color: "rgb(144, 144, 144)" }}
                        >{`Aperçu de l'application selon le rôle. Naviguer et tester les fonctionnalités avec les permissions du rôle sélectionné`}</div>
                    </div>
                    <div className="setting-right" style={{ marginTop: "0.5rem" }}>
                        <ChipFilter
                            options={rolesPreviewOptions}
                            label="Naviguer en tant que :"
                            value={rolePreview ?? defaultRolePreview}
                            handleChooseOption={handleChooseRolePreview}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountSettings;
